// Academy WATCH — subscriber-only Netflix-style browse. Hero featured + rows
// by category + command-bar search. Playback gated by active subscription.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Search, Clock, Film, Sparkles, ArrowRight, X } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const tk = raw ? (JSON.parse(raw)?.token) : null;
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  } catch { return {}; }
}

const CATEGORY_SLUGS = ["wine_insights", "guides", "get_started", "for_professionals"];

const fmtDuration = (sec) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}min` : `${m} min`;
};

const sub = (s, vars) => Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

const AcademyWatchPage = () => {
  const t = useT();
  const { user } = useAuth();
  const [hasSub, setHasSub] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  const CATEGORIES = useMemo(() => CATEGORY_SLUGS.map((slug) => ({ slug, label: t(`academyW.cat.${slug}`) })), [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { if (!cancelled) setHasSub(false); return; }
      const { ok, data } = await apiFetch(`/api/auth/me/subscription`, { headers: authHeaders() });
      if (!cancelled) setHasSub(ok && !!data?.active);
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [f, s] = await Promise.all([
        apiFetch(`/api/academy/featured`),
        apiFetch(`/api/academy/series`),
      ]);
      if (cancelled) return;
      setFeatured(f.ok ? (f.data?.series || null) : null);
      setSeries(s.ok ? (s.data?.items || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSearchChange = (q) => {
    setSearchQ(q);
    if (window.__gvAcademySearchTid) clearTimeout(window.__gvAcademySearchTid);
    if (!q.trim()) { setSearchResults([]); return; }
    window.__gvAcademySearchTid = setTimeout(async () => {
      const { ok, data } = await apiFetch(`/api/academy/search?q=${encodeURIComponent(q.trim())}`);
      setSearchResults(ok ? (data?.items || []) : []);
    }, 200);
  };

  const byCategory = useMemo(() => {
    const groups = {};
    CATEGORY_SLUGS.forEach((c) => { groups[c] = []; });
    series.forEach((s) => {
      if (groups[s.category]) groups[s.category].push(s);
    });
    return groups;
  }, [series]);

  const visibleCategories = activeCategory
    ? CATEGORIES.filter((c) => c.slug === activeCategory)
    : CATEGORIES;

  return (
    <Layout testId="page-academy-watch">
      <SEO title={t("academyW.seo.title")} description={t("academyW.seo.desc")} />

      {hasSub === false && (
        <div className="bg-[color:var(--gv-pink)] text-white" data-testid="academy-watch-upsell-banner">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-col sm:flex-row items-center gap-3 sm:justify-between text-[13.5px]">
            <div className="flex items-center gap-2">
              <Sparkles size={14} />
              <span>{t("academyW.upsell.text")}</span>
            </div>
            <a href="https://app.geovinum.io" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white text-[color:var(--gv-pink)] px-4 py-1.5 rounded-[5px] font-medium hover:bg-white/90 transition-colors whitespace-nowrap" data-testid="academy-watch-upsell-cta">
              {t("academyW.upsell.cta")} <ArrowRight size={12} />
            </a>
          </div>
        </div>
      )}

      <section className="relative bg-[color:var(--gv-blue)] text-white overflow-hidden" data-testid="academy-hero">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        {featured?.hero_image_url && (
          <div className="absolute inset-0 opacity-25">
            <img src={featured.hero_image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gv-blue)] via-[color:var(--gv-blue)]/85 to-transparent" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-white/15 backdrop-blur text-[11px] uppercase tracking-widest mb-5" data-testid="academy-hero-eyebrow">
            <Sparkles size={12} /> {featured ? t("academyW.hero.eyebrow_featured") : "geoVINUM Academy"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4 max-w-3xl" data-testid="academy-hero-title">
            {featured ? featured.title : t("academyW.hero.default_title")}
          </h1>
          <p className="text-[15px] md:text-[16px] text-white/85 leading-relaxed max-w-2xl mb-7" data-testid="academy-hero-desc">
            {featured ? featured.description : t("academyW.hero.default_desc")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {featured ? (
              <Link to={`/academy/series/${featured.slug}`} className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink-dark)] text-white px-5 py-3 rounded-[5px] font-medium transition-colors" data-testid="academy-hero-cta-watch">
                <Play size={14} fill="currentColor" /> {t("academyW.hero.watch")}
              </Link>
            ) : (
              <a href="https://app.geovinum.io" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink-dark)] text-white px-5 py-3 rounded-[5px] font-medium transition-colors">
                <Sparkles size={14} /> {t("academyW.hero.trial30")}
              </a>
            )}
            <button onClick={() => setSearchOpen(true)} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white px-5 py-3 rounded-[5px] text-[14px] transition-colors" data-testid="academy-search-trigger">
              <Search size={14} /> {t("academyW.hero.search_cta")}
            </button>
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--gv-blue)] border-b border-white/10 sticky top-[72px] z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-2 overflow-x-auto" data-testid="academy-category-bar">
          <button
            onClick={() => setActiveCategory(null)}
            className={`shrink-0 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${!activeCategory ? "bg-white text-[color:var(--gv-blue)]" : "bg-white/10 text-white hover:bg-white/20"}`}
            data-testid="academy-chip-all"
          >{t("academyW.chip.all")}</button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActiveCategory(c.slug)}
              className={`shrink-0 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${activeCategory === c.slug ? "bg-white text-[color:var(--gv-blue)]" : "bg-white/10 text-white hover:bg-white/20"}`}
              data-testid={`academy-chip-${c.slug}`}
            >
              {c.label} <span className="opacity-60 ml-1">({(byCategory[c.slug] || []).length})</span>
            </button>
          ))}
        </div>
      </section>

      <div className="bg-[color:var(--gv-blue)] pb-20">
        {loading && (
          <div className="text-center py-20 text-white/60">{t("academyW.loading")}</div>
        )}
        {!loading && series.length === 0 && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center text-white">
            <Film size={36} className="mx-auto text-white/30 mb-4" />
            <div className="font-display text-2xl mb-2">{t("academyW.empty.title")}</div>
            <div className="text-[14px] text-white/65">{t("academyW.empty.desc")}</div>
          </div>
        )}
        {visibleCategories.map((cat) => {
          const items = byCategory[cat.slug] || [];
          if (items.length === 0) return null;
          const countLabel = items.length > 1 ? t("academyW.series_count_other") : t("academyW.series_count_one");
          return (
            <section key={cat.slug} className="max-w-7xl mx-auto px-6 lg:px-10 pt-12" data-testid={`academy-row-${cat.slug}`}>
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-display text-2xl md:text-3xl text-white">{cat.label}</h2>
                <span className="text-[12px] text-white/55">{items.length} {countLabel}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((s) => <SeriesCard key={s.slug} s={s} t={t} />)}
              </div>
            </section>
          );
        })}
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-[color:var(--gv-ink)]/85 backdrop-blur-sm flex items-start justify-center p-6 pt-20" onClick={() => setSearchOpen(false)} data-testid="academy-search-overlay">
          <div className="w-full max-w-2xl bg-white rounded-[5px] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--gv-line)]">
              <Search size={16} className="text-[color:var(--gv-ink)]/50" />
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t("academyW.search.placeholder")}
                className="flex-1 outline-none text-[15px]"
                data-testid="academy-search-input"
              />
              <button onClick={() => setSearchOpen(false)} className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]"><X size={18} /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {searchResults.length === 0 && searchQ.trim() && (
                <div className="px-4 py-8 text-center text-[13px] text-[color:var(--gv-ink)]/55">{sub(t("academyW.search.no_results"), { q: searchQ })}</div>
              )}
              {searchResults.map((s) => (
                <Link
                  key={s.slug}
                  to={`/academy/series/${s.slug}`}
                  onClick={() => setSearchOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)] last:border-0"
                  data-testid={`academy-search-result-${s.slug}`}
                >
                  <div className="w-14 h-14 shrink-0 rounded-[5px] bg-[color:var(--gv-mist)] overflow-hidden">
                    {s.hero_image_url ? <img src={s.hero_image_url} alt={s.title} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium text-[color:var(--gv-ink)] truncate">{s.title}</div>
                    <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">{s.videos_count} {t("academyW.videos_label")} · {fmtDuration(s.total_length_sec)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const SeriesCard = ({ s }) => (
  <Link
    to={`/academy/series/${s.slug}`}
    className="group block bg-white/5 border border-white/10 rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] hover:bg-white/10 transition-colors"
    data-testid={`academy-series-card-${s.slug}`}
  >
    <div className="aspect-video bg-black/30 relative overflow-hidden">
      {s.hero_image_url ? (
        <img src={s.hero_image_url} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/30">
          <Film size={28} />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
        <Play size={18} className="text-white" fill="currentColor" />
      </div>
    </div>
    <div className="p-3">
      <div className="font-display text-[14px] text-white mb-1 line-clamp-2 group-hover:text-[color:var(--gv-pink)] transition-colors">{s.title}</div>
      <div className="text-[11px] text-white/55 flex items-center gap-2">
        <Film size={11} /> {s.videos_count}
        <Clock size={11} className="ml-1" /> {fmtDuration(s.total_length_sec)}
      </div>
    </div>
  </Link>
);

export default AcademyWatchPage;
